import React, { useState, useEffect } from 'react';
import './SmartInboxEmail.css';
import './EmailPopup.css';
import { database, firestore } from '../firebaseConfig';
import { ref, get, update, remove, set, onValue } from 'firebase/database';
import { collection, query, where, getDocs, updateDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import EmailAiPopup from './EmailAiPopup';
import EmailSentNotifier from './EmailSentNotifier';
import SetTagPopup from './SetTagPopup';
import SentMailFrame from './SentMailFrame';
import { getFunctions, httpsCallable } from 'firebase/functions';
import quotedPrintable from 'quoted-printable';
import he from 'he';
import ReactDOM from 'react-dom';
import { Letter } from 'react-letter';
import { CONSUMER_EMAIL_PROVIDERS, TAG_IDS } from '../shared/enums';
import { authenticatedFetch, getCloudFunctionUrl } from '../utils/cloudFunctions';

// Touchpoint-Gruppen für "Add to Leads" Modal
const TOUCHPOINT_GROUPS = {
  interest: {
    label: "Interest Level",
    icon: "fa-thermometer-half",
    items: {
      general_interest: { label: "General Interest", icon: "fa-thumbs-up", points: 2 },
      highly_interested: { label: "Highly Interested", icon: "fa-fire", points: 3 },
      not_now_but_friendly: { label: "Not Now, But Friendly", icon: "fa-clock", points: 1 },
      requested_info: { label: "Requested Info", icon: "fa-info-circle", points: 2 },
    }
  },
  meetings: {
    label: "Meetings & Calls",
    icon: "fa-calendar-check",
    items: {
      zoom_call_scheduled: { label: "Zoom Call Scheduled", icon: "fa-video", points: 3 },
      meet_in_person: { label: "Meet In Person", icon: "fa-users", hasCount: true, points: 4 },
      online_meeting: { label: "Online Meeting", icon: "fa-video", hasCount: true, points: 3 },
      phone_call: { label: "Phone Call", icon: "fa-phone-alt", hasCount: true, points: 2 },
    }
  },
  sales: {
    label: "Sales Process",
    icon: "fa-chart-line",
    items: {
      demo_given: { label: "Demo Given", icon: "fa-play-circle", hasCount: true, points: 3 },
      proposal_sent: { label: "Proposal Sent", icon: "fa-file-alt", hasCount: true, points: 3 },
      contract_sent: { label: "Contract Sent", icon: "fa-file-contract", points: 3 },
      interested_in_groupchat: { label: "Interested in Groupchat", icon: "fa-comments", points: 2 },
    }
  },
  linkedin: {
    label: "LinkedIn",
    icon: "fa-linkedin",
    isBrand: true,
    items: {
      linkedin_connected: { label: "Connected", icon: "fa-linkedin", isBrand: true, points: 1 },
      linkedin_message: { label: "Message Sent", icon: "fa-linkedin", isBrand: true, hasCount: true, points: 1 },
    }
  },
  content: {
    label: "Content & Events",
    icon: "fa-calendar-alt",
    items: {
      loom_video: { label: "Loom Video Sent", icon: "fa-video", hasCount: true, points: 2 },
      webinar_attended: { label: "Webinar Attended", icon: "fa-desktop", hasCount: true, points: 2 },
    }
  },
};

const SmartInboxEmail = ({ email, userId, emailType = 'inbox', onDelete, onMarkAsReplied, onProcessWithAgents, autoProcessEnabled }) => {
  const [expanded, setExpanded] = useState(false);
  const [isRead, setIsRead] = useState(email.isRead || false);
  const [isReplied, setIsReplied] = useState(email.isReplied || false);
  const [loading, setLoading] = useState(false);
  const [showEmailAiPopup, setShowEmailAiPopup] = useState(false);
  const [emailReplyData, setEmailReplyData] = useState(null);
  const [hasReplied, setHasReplied] = useState(false);
  const [serverSyncStatus, setServerSyncStatus] = useState('');
  const [emailNotifier, setEmailNotifier] = useState({
    status: '',
    message: ''
  });
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [htmlViewMode, setHtmlViewMode] = useState('iframe'); // 'iframe' oder 'parsed'
  const [processingWithAgents, setProcessingWithAgents] = useState(false);
  const [showForwardEmailInput, setShowForwardEmailInput] = useState(false);
  const [forwardEmailAddress, setForwardEmailAddress] = useState('');
  const [showSetTagPopup, setShowSetTagPopup] = useState(false);
  const [tagUpdateTrigger, setTagUpdateTrigger] = useState(0);
  const [blacklisting, setBlacklisting] = useState(false);
  const [addingToLeads, setAddingToLeads] = useState(false);
  const [showAddToLeadsModal, setShowAddToLeadsModal] = useState(false);
  const [selectedTouchpoints, setSelectedTouchpoints] = useState({});
  const [customTouchpoints, setCustomTouchpoints] = useState([]);

  // Hilfsfunktion für konsistente E-Mail-ID
  const getEmailId = () => {
    return email.id || email.messageId || `temp_${Date.now()}`;
  };

  // Prüfe, ob es sich um eine gesendete E-Mail handelt
  const isSentEmail = email.isSent === true;

  // Setze den Standard-Anzeigemodus basierend auf verfügbaren Daten
  useEffect(() => {
    if (email.htmlStorageUrl) {
      setHtmlViewMode('iframe');
    } else {
      setHtmlViewMode('parsed');
    }
  }, [email.htmlStorageUrl]);

  // Lade Custom Touchpoints aus Firebase
  useEffect(() => {
    if (!userId) return;
    
    const customRef = ref(database, `World/${userId}/settings/customTouchpoints`);
    const unsubscribe = onValue(customRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = [];
        snapshot.forEach((childSnapshot) => {
          data.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setCustomTouchpoints(data);
      } else {
        setCustomTouchpoints([]);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Firebase Functions
  // markEmailAsRead öffnet IMAP und läuft deshalb in Europa.
  const functions = getFunctions(undefined, 'europe-west1');
  const markEmailAsReadFunc = httpsCallable(functions, 'markEmailAsRead');

  // Formatiere das Datum für bessere Lesbarkeit
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      
      // Format the time (HH:MM)
      const time = date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit'
      });
      
      // Format the date (MM/DD/YYYY)
      const formattedDate = date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      return { time, date: formattedDate };
    } catch (err) {
      console.error('Error formatting date:', err);
      return { time: '', date: dateString };
    }
  };
  
  // Extrahiere den Namen und die E-Mail-Adresse aus dem Absender/Empfänger
  const parseFromField = (fromString) => {
    if (!fromString) return { name: 'Unknown', email: '', hasName: false };
    
    try {
      // Versuche, Name und E-Mail-Adresse zu extrahieren
      const match = fromString.match(/^(.*?)\s*<(.+?)>$/);
      if (match) {
        const name = match[1].trim();
        return {
          name: name,
          email: match[2].trim(),
          hasName: name.length > 0 // Hat einen Namen, wenn das Namen-Feld nicht leer ist
        };
      }
      
      // Wenn es nur eine E-Mail-Adresse ohne Namen ist
      if (fromString.includes('@')) {
        return {
          name: fromString,
          email: fromString.trim(),
          hasName: false // Keine Name, nur E-Mail-Adresse
        };
      }
      
      // Wenn kein Match und keine E-Mail-Adresse, gib den gesamten String als Name zurück
      return {
        name: fromString,
        email: '',
        hasName: true // Wir betrachten den unbekannten String als Namen
      };
    } catch (err) {
      console.error('Error parsing email address:', err);
      return { name: fromString, email: '', hasName: false };
    }
  };

  const { time, date } = formatDate(email.date);
  
  // Bei gesendeten E-Mails zeigen wir den Empfänger statt des Absenders an
  const fromOrToField = (isSentEmail || emailType === 'sent' || emailType === 'replied' || emailType === 'forwarded') 
    ? (email.to || email.recipients || '') 
    : email.from;
  const displayPerson = parseFromField(fromOrToField);

  // Bei gesendeten E-Mails zusätzlich den Absender parsen
  const senderPerson = (isSentEmail || emailType === 'sent' || emailType === 'replied' || emailType === 'forwarded') 
    ? parseFromField(email.from) 
    : null;

  // Zeige die richtige E-Mail-Adresse im Badge - je nach E-Mail-Typ
  const badgeEmail = isSentEmail 
    ? (email.from ? parseFromField(email.from).email : '')  // Bei gesendeten E-Mails: from
    : (email.to ? parseFromField(email.to).email : (email.accountEmail || ''));  // Bei empfangenen E-Mails: to
  
  const accountEmail = badgeEmail || 'Unknown account';
  
  // Formatiere E-Mail-Adresse für die Anzeige (max. 20 Zeichen)
  const formatShortEmail = (email) => {
    if (!email || email.length <= 20) return email;
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    
    const username = parts[0];
    const domain = parts[1];
    
    // Kürzen des Benutzernamens, wenn zu lang
    const shortUsername = username.length > 10 
      ? username.substring(0, 8) + '...' 
      : username;
    
    // Kürzen der Domain, wenn zu lang
    const shortDomain = domain.length > 8
      ? domain.substring(0, 6) + '...'
      : domain;
    
    return `${shortUsername}@${shortDomain}`;
  };
  
  const displayAccountEmail = formatShortEmail(accountEmail);
  
  // Prüfe, ob die E-Mail aus dem Spam-Ordner stammt
  const isSpam = email.isSpam || false;

  // Begeisterungsgrad-Anzeige
  const getEnthusiasmScore = () => {
    if (email.enthusiasmScore === undefined) {
      return {
        score: null,
        className: 'enthusiasm_score_unknown',
        text: 'No rating'
      };
    }
    
    const score = parseInt(email.enthusiasmScore, 10);
    
    if (score <= 3) {
      return {
        score,
        className: 'enthusiasm_score_negative',
        text: `${score}/10`
      };
    } else if (score <= 7) {
      return {
        score,
        className: 'enthusiasm_score_neutral',
        text: `${score}/10`
      };
    } else {
      return {
        score,
        className: 'enthusiasm_score_positive',
        text: `${score}/10`
      };
    }
  };
  
  const enthusiasmInfo = getEnthusiasmScore();

  // Prüfe beim Laden, ob diese E-Mail bereits beantwortet wurde
  useEffect(() => {
    // Bei gesendeten E-Mails brauchen wir nicht zu prüfen, ob sie beantwortet wurden
    if (isSentEmail) return;
    
    const checkIfReplied = async () => {
      try {
        const emailId = getEmailId();
        
        // ✅ NEU: Prüfe direkt das isReplied-Feld der E-Mail anstatt emailReplies in RTDB
        // Setze Status nur wenn explizit definiert (nicht undefined)
        if (email && typeof email.isReplied === 'boolean') {
          setHasReplied(email.isReplied);
          setIsReplied(email.isReplied);
          console.log(`📧 [REPLIED-STATUS] Email ${emailId} reply status: ${email.isReplied}`);
        } else if (email) {
          console.log(`📧 [NO-STATUS] Email ${emailId} has no defined reply status - keeping current state`);
        }
        
      } catch (error) {
        console.error("Error checking reply status:", error);
      }
    };
    
    if (email && getEmailId()) {
      checkIfReplied();
    }
  }, [email, userId, isSentEmail, onMarkAsReplied, emailType]);

  // Eine Funktion, die aufgerufen wird, wenn auf die E-Mail geklickt wird
  const toggleExpand = async () => {
    const wasExpanded = expanded;
    setExpanded(!wasExpanded);
    
    // Gesendete E-Mails sind immer als gelesen markiert
    if (isSentEmail) return;
    
    // 🛡️ [SAFEGUARD] SafeGuard Drafts müssen nicht als "gelesen" markiert werden
    if (emailType === 'safeguard') {
      console.log('🛡️ [SAFEGUARD] Skipping markAsRead for SafeGuard draft');
      return;
    }
    
    // Nur als gelesen markieren, wenn die E-Mail noch nicht als gelesen markiert ist
    if (!wasExpanded && !isRead) {
      console.log(`Marking email "${email.subject}" as read`);
      
      const emailId = getEmailId();
      setIsRead(true);
      
      try {
        // Aktualisiere den Status in der Datenbank und auf dem Server
        const result = await markEmailAsReadFunc({ 
          emailId: emailId,
          emailType: emailType, // ✅ Übergebe den emailType an die Cloud Function
          userId: userId // 🔒 TEAM SUPPORT: Übergebe userId für Team-Mitglieder
        });
        
        // Überprüfe, ob die E-Mail auch auf dem Server als gelesen markiert wurde
        if (result.data.serverUpdated) {
          console.log("Email successfully marked as read on the server");
          setServerSyncStatus('synced');
        } else if (result.data.error) {
          console.warn("Email could not be updated on server:", result.data.error);
          setServerSyncStatus('error');
        } else {
          console.log("Email only updated locally");
          setServerSyncStatus('local');
        }
      } catch (error) {
        console.error("Error marking as read:", error);
        setServerSyncStatus('error');
      }
    }
  };

  // Funktion zum Soft-Delete der E-Mail (markiert als deleted)
  const handleDelete = async () => {
    const emailId = getEmailId();
    if (!userId || !email || !emailId) return;
    
    try {
      setDeleting(true);
      
      // 🛡️ [SAFEGUARD] Spezielle Behandlung für SafeGuard Drafts
      if (emailType === 'safeguard') {
        console.log('🛡️ [SAFEGUARD-DELETE] Deleting SafeGuard draft from Firestore:', emailId);
        
        // SafeGuard Drafts sind in Firestore gespeichert
        const { deleteDoc, doc, getFirestore } = await import('firebase/firestore');
        const firestore = getFirestore();
        
        const draftDocRef = doc(firestore, 'world_drafts', userId, 'emails', emailId);
        await deleteDoc(draftDocRef);
        
        console.log(`🛡️ [SAFEGUARD-DELETE] SafeGuard draft ${emailId} successfully deleted from Firestore`);
        
        // Markiere die E-Mail explizit als SafeGuard Draft für bessere Erkennung
        const safeGuardDraftEmail = {
          ...email,
          isSafeGuardDraft: true,
          reason: 'safeguard_rejection',
          emailType: 'safeguard'
        };
        
        // Rufe onDelete auf, damit die übergeordnete Komponente die E-Mail aus der Anzeige entfernt
        if (onDelete) {
          console.log('🛡️ [SAFEGUARD-DELETE] Calling onDelete with SafeGuard draft data');
          onDelete(safeGuardDraftEmail);
        }
        
        return;
      }
      
      // Normale E-Mails: Soft-Delete direkt in der entsprechenden Firestore Collection
      if (emailType === 'inbox' || emailType === 'sent' || emailType === 'replied' || emailType === 'forwarded') {
        console.log('📧 [EMAIL-DELETE] Marking email as deleted in Firestore:', emailId, 'Type:', emailType);
        
        // Bestimme die richtige Collection basierend auf emailType
        let collectionName;
        switch(emailType) {
          case 'sent':
            collectionName = 'world_sent';
            break;
          case 'replied':
            collectionName = 'world_sent'; // Replied emails sind auch in world_sent
            break;
          case 'forwarded':
            collectionName = 'world_forwarded';
            break;
          case 'inbox':
          default:
            collectionName = 'world_inbox';
            break;
        }
        
        console.log(`📧 [EMAIL-DELETE] Using collection: ${collectionName}`);
        
        // Firestore direkt verwenden
        const { updateDoc, doc, getFirestore, serverTimestamp } = await import('firebase/firestore');
        const firestore = getFirestore();
        
        const emailDocRef = doc(firestore, collectionName, userId, 'emails', emailId);
        await updateDoc(emailDocRef, {
          deleted: true,
          deletedAt: serverTimestamp()
        });
        
        console.log(`📧 [EMAIL-DELETE] Email ${emailId} successfully marked as deleted in ${collectionName}`);
        
        // Rufe onDelete auf, damit die übergeordnete Komponente die E-Mail aus der Anzeige entfernt
        if (onDelete) {
          onDelete(email);
        }
        
        return;
      }
      
      // KEIN Fallback mehr - alle E-Mails sollten in Firestore sein
      console.warn('📧 [EMAIL-DELETE] Unknown email type:', emailType, 'Email data:', email);
      console.warn('📧 [EMAIL-DELETE] This should not happen - all emails should be in Firestore collections');
      
      // Statt in RTDB zu schreiben, loggen wir nur eine Warnung
      throw new Error(`Unsupported email type for deletion: ${emailType}. Email should be in Firestore, not Realtime Database.`);
      
    } catch (error) {
      console.error('Fehler beim Löschen der E-Mail in Firestore:', error);
      setError(`Error deleting email: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Funktion zum Wiederherstellen der E-Mail (entfernt deleted Status)
  const handleRecover = async () => {
    const emailId = getEmailId();
    if (!userId || !email || !emailId) return;
    
    try {
      setDeleting(true);
      
      // NEU: Verwende Firestore statt RTDB
      console.log(`📧 [EMAIL-RECOVER] Recovering email ${emailId} from deleted status in Firestore`);
      
      // Bestimme die richtige Collection basierend auf emailType
      let collectionName;
      switch(emailType) {
        case 'sent':
          collectionName = 'world_sent';
          break;
        case 'replied':
          collectionName = 'world_sent'; // Replied emails sind auch in world_sent
          break;
        case 'forwarded':
          collectionName = 'world_forwarded';
          break;
        case 'scheduled':
          collectionName = 'world_scheduled_emails';
          break;
        case 'safeguard':
          collectionName = 'world_drafts';
          break;
        case 'inbox':
        default:
          collectionName = 'world_inbox';
          break;
      }
      
      console.log(`📧 [EMAIL-RECOVER] Using collection: ${collectionName}`);
      
      // Firestore direkt verwenden
      const { updateDoc, doc, getFirestore } = await import('firebase/firestore');
      const firestore = getFirestore();
      
      const emailDocRef = doc(firestore, collectionName, userId, 'emails', emailId);
      await updateDoc(emailDocRef, {
        deleted: false, // Setze auf false statt null
        deletedAt: null
      });
      
      console.log(`📧 [EMAIL-RECOVER] Email ${emailId} successfully recovered in ${collectionName}`);
      
      // Rufe onDelete auf, damit die übergeordnete Komponente die E-Mail aus der Deleted-Anzeige entfernt
      if (onDelete) {
        onDelete(email);
      }
      
    } catch (error) {
      console.error('📧 [EMAIL-RECOVER] Error recovering email in Firestore:', error);
      setError(`Error recovering email: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Funktion zum Antworten auf eine E-Mail (nicht für gesendete E-Mails)
  const handleReplyWithAi = async () => {
    if (!email || isSentEmail) return;
    
    // Prüfe ob Email auf Blacklist ist
    const extractEmailAddress = (fromString) => {
      if (!fromString) return '';
      const emailMatch = fromString.match(/<([^>]+)>/) || fromString.match(/([^\s@]+@[^\s@]+\.[^\s@]+)/);
      return emailMatch ? (emailMatch[1] || emailMatch[0]).trim().toLowerCase() : '';
    };
    
    const emailAddress = extractEmailAddress(email.from);
    if (emailAddress) {
      try {
        const blacklistDocRef = doc(firestore, `World/${userId}/blacklist/${emailAddress}`);
        const blacklistDoc = await getDoc(blacklistDocRef);
        
        if (blacklistDoc.exists()) {
          console.log(`🚫 [BLACKLIST] Cannot reply - email is blacklisted: ${emailAddress}`);
          setError(`❌ Cannot reply to "${emailAddress}" - this email is on your blacklist.`);
          return;
        }
      } catch (error) {
        console.error('Error checking blacklist:', error);
      }
    }
    
    // Prüfe, ob die E-Mail Tags hat und ob einer davon ein vorausgewähltes Topic hat
    let selectedTopic = "";
    if (email.tags && Array.isArray(email.tags)) {
      // Lade die AI Tag Definitionen
      const aiTagsRef = ref(database, `/World/${userId}/settings/aiTags`);
      const snapshot = await get(aiTagsRef);
      
      if (snapshot.exists()) {
        const aiTags = [];
        snapshot.forEach((childSnapshot) => {
          aiTags.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        
        // Suche nach einem Tag mit selectedTopic
        for (const tag of email.tags) {
          const tagDefinition = aiTags.find(aiTag => aiTag.tag === tag);
          if (tagDefinition && tagDefinition.selectedTopic) {
            selectedTopic = tagDefinition.selectedTopic;
            break;
          }
        }
      }
    }
    
    // Erstelle ein Objekt mit den Daten, die EmailAiPopup benötigt
    const replyData = {
      id: getEmailId(),
      business_name: displayPerson.name,
      lead_email: displayPerson.email,
      lead_description: email.bodyText || email.preview || "",
      // Füge weitere Felder hinzu, die EmailAiPopup erwartet
      lead_website: "",
      lead_instagram: "",
      lead_tiktok: "",
      lead_type: "email_reply",
      // Speichere die ursprüngliche E-Mail für den Kontext
      original_email: {
        subject: email.subject,
        content: email.bodyText || email.preview || "",
        from: email.from,
        date: email.date
      },
      // Füge die Konto-Informationen hinzu
      accountEmail: email.accountEmail,
      accountId: email.accountId,
      // Füge das vorausgewählte Topic hinzu
      selectedTopic: selectedTopic
    };
    
    setEmailReplyData(replyData);
    setShowEmailAiPopup(true);
  };

  // Funktion zum Weiterleiten einer E-Mail an eine neue Adresse
  const handleForwardWithAi = async () => {
    if (!email) return;
    
    // Zeige zuerst das Eingabefeld für die E-Mail-Adresse
    setShowForwardEmailInput(true);
  };

  // Funktion zum manuellen Senden für SafeGuard Drafts
  const handleSendManually = async () => {
    try {
      console.log('🛡️ [SEND-MANUALLY] Opening SafeGuard draft for manual editing:', email);
      
      // Extrahiere den ursprünglichen E-Mail-Content aus verschiedenen möglichen Feldern
      const originalContent = email.originalEmailContent || 
                             email.content || 
                             email.bodyText || 
                             email.text || 
                             email.body || 
                             email.email_content || 
                             "";
      
      console.log('🛡️ [SEND-MANUALLY] Extracted original content:', {
        length: originalContent.length,
        preview: originalContent.substring(0, 100) + '...',
        fields: {
          originalEmailContent: !!email.originalEmailContent,
          content: !!email.content,
          bodyText: !!email.bodyText,
          text: !!email.text,
          body: !!email.body,
          email_content: !!email.email_content
        }
      });
      
      // Erstelle eine Kopie der ursprünglichen E-Mail-Daten für die Bearbeitung
      const originalEmailData = {
        id: `manual_${Date.now()}`,
        business_name: email.businessName || "",
        lead_email: email.originalTo || email.to || email.recipients || "",
        lead_description: originalContent,
        lead_website: "",
        lead_instagram: "",
        lead_tiktok: "",
        lead_type: "safeguard_retry",
        // Original Subject für Betreff (OHNE "Re:" für SafeGuard Retry)
        original_email: {
          subject: email.subject || "(No Subject)",
          content: originalContent,
          from: email.accountEmail || email.from || "Unknown",
          to: email.originalTo || email.to || email.recipients || "",
          date: new Date().toISOString(),
          recipients: email.originalTo || email.to || email.recipients || "",
          accountEmail: email.accountEmail,
          isSafeGuardRetry: true,
          originalEmailId: getEmailId(),
          // SafeGuard-Informationen für Kontext
          safeGuardInfo: email.safeGuardInfo,
          safeGuardRating: email.safeGuardRating,
          safeGuardIssues: email.safeGuardIssues,
          safeGuardImprovements: email.safeGuardImprovements
        },
        // Konto-Informationen
        accountEmail: email.accountEmail || email.from || "",
        accountId: email.accountId,
        to: email.originalTo || email.to || email.recipients || "",
        recipients: email.originalTo || email.to || email.recipients || "",
        leadId: email.leadId || null,
        selectedTopic: "",
        // Setze den ursprünglichen E-Mail-Content als vorgefüllten Inhalt
        prefilledBodyContent: originalContent,
        skipGeneration: true,
        isSafeGuardRetry: true,
        // Zusätzliche Flags für direkte Content-Anzeige
        usePrefilledContent: true,
        showOriginalContent: true,
        // WICHTIG: Verhindere "Re:" im Betreff für SafeGuard Retry
        preventReplyPrefix: true,
        originalSubject: email.subject || "(No Subject)"
      };
      
      console.log('🛡️ [SEND-MANUALLY] Opening EmailAiPopup with data:', originalEmailData);
      
      // Setze die E-Mail-Daten für das Popup
      setEmailReplyData(originalEmailData);
      setShowEmailAiPopup(true);
      
    } catch (error) {
      console.error('🛡️ [SEND-MANUALLY] Error opening SafeGuard draft:', error);
    }
  };

  // Funktion zum Bestätigen der Forward-E-Mail-Adresse
  const handleForwardEmailSubmit = () => {
    if (!forwardEmailAddress.trim()) {
      setEmailNotifier({
        status: 'error',
        message: 'Please enter an email address'
      });
      setTimeout(() => resetEmailNotifier(), 3000);
      return;
    }

    // Verbesserte E-Mail-Validierung
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(forwardEmailAddress.trim())) {
      setEmailNotifier({
        status: 'error',
        message: 'Please enter a valid email address'
      });
      setTimeout(() => resetEmailNotifier(), 3000);
      return;
    }
    
    // Erstelle den E-Mail-Verlauf für die Weiterleitung als fertigen Content
    let emailBodyContent = email.bodyText || email.preview || email.content || 'No content available';
    
    // Begrenze den Content auf 10000 Zeichen um Database-Limits zu vermeiden
    if (emailBodyContent.length > 10000) {
      emailBodyContent = emailBodyContent.substring(0, 10000) + '\n\n[Content truncated due to length...]';
    }
    
    const forwardedEmailContent = `
--- Forwarded Email ---
From: ${email.from || 'Unknown'}
To: ${email.to || email.accountEmail || 'Unknown'}
Date: ${email.date || 'Unknown'}
Subject: ${email.subject || '(No Subject)'}

${emailBodyContent}
--- End of Forwarded Email ---
    `.trim();
    
    // Verbesserte Temp-ID Generierung mit besserer Eindeutigkeit
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 9);
    const emailId = getEmailId() || 'unknown';
    const tempId = `temp_forward_${emailId}_${timestamp}_${randomSuffix}`;
    
    // Erstelle ein Objekt mit den Daten für die Weiterleitung
    const forwardData = {
      id: tempId,
      business_name: "", // Leer lassen für neue Eingabe
      lead_email: forwardEmailAddress.trim(), // Die eingegebene E-Mail-Adresse
      lead_description: "", // Leer lassen, da wir den Content direkt setzen
      // Weitere Felder
      lead_website: "",
      lead_instagram: "",
      lead_tiktok: "",
      lead_type: "email_forward",
      // Flag für Forward-Erkennung
      isForwardOperation: true,
      // Ursprüngliche E-Mail für Kontext
      original_email: {
        subject: `Fwd: ${email.subject || '(No Subject)'}`,
        content: forwardedEmailContent,
        from: email.accountEmail || email.from || 'Unknown', // Absender ist das Account das weiterleitet
        to: forwardEmailAddress.trim(), // Empfänger ist die neue Forward-Adresse
        date: new Date().toISOString(), // Aktuelles Datum für die Weiterleitung
        // Zusätzliche Felder für vollständige Kompatibilität
        recipients: forwardEmailAddress.trim(),
        accountEmail: email.accountEmail,
        isForward: true,
        originalEmailId: getEmailId() // Referenz zur ursprünglichen E-Mail
      },
      // Konto-Informationen vom aktuellen Benutzer
      accountEmail: email.accountEmail,
      accountId: email.accountId,
      // Überschreibe die "to" Adresse für die Weiterleitung
      to: forwardEmailAddress.trim(),
      recipients: forwardEmailAddress.trim(),
      // Kein selectedTopic für Forward
      selectedTopic: "",
      // WICHTIG: Setze den E-Mail-Content direkt als Body-Content
      prefilledBodyContent: forwardedEmailContent,
      skipGeneration: true // Flag um automatische Generierung zu überspringen
    };
    
    // Schließe das E-Mail-Input und öffne das EmailAiPopup
    setShowForwardEmailInput(false);
    setForwardEmailAddress('');
    
    // Debug: Logge die Forward-Daten
    console.log('🔄 [FORWARD] Forward data being sent to EmailAiPopup:', forwardData);
    console.log('🔄 [FORWARD] Forward email address:', forwardEmailAddress.trim());
    console.log('🔄 [FORWARD] Prefilled body content length:', forwardedEmailContent.length);
    
    setEmailReplyData(forwardData);
    setShowEmailAiPopup(true);
  };

  // Funktion zum Abbrechen der Forward-Eingabe
  const handleForwardEmailCancel = () => {
    setShowForwardEmailInput(false);
    setForwardEmailAddress('');
  };

  // Funktion zum Herunterladen von E-Mail-Anhängen.
  //
  // Two attachment shapes need to coexist here:
  //   1. Inbound (IMAP-fetched) — has `email.uid` and an attachmentIndex; bytes
  //      live in the IMAP server, so we route through the downloadEmailAttachment
  //      cloud function.
  //   2. Outbound (sent by our composer) — has `attachment.storagePath`; bytes
  //      live in Firebase Storage. Resolve a fresh download URL via the SDK so
  //      we never persist a long-lived public URL in Firestore.
  const downloadAttachment = async (attachmentIndex, attachment) => {
    const restoreIcon = () => {
      const el = document.querySelector(`[data-attachment-index="${attachmentIndex}"] .smart_email_attachment_download`);
      if (el) el.textContent = '⬇️';
    };
    const setLoadingIcon = () => {
      const el = document.querySelector(`[data-attachment-index="${attachmentIndex}"] .smart_email_attachment_download`);
      if (el) el.textContent = '⏳';
    };

    try {
      console.log(`📎 [ATTACHMENT-DOWNLOAD] Starting download for attachment ${attachmentIndex}:`, attachment);
      setLoadingIcon();

      // ── Outbound attachments (Firebase Storage) ──
      if (attachment && attachment.storagePath) {
        try {
          const { getStorage, ref: storageRef, getDownloadURL } = await import('firebase/storage');
          const url = await getDownloadURL(storageRef(getStorage(), attachment.storagePath));
          const link = document.createElement('a');
          link.href = url;
          link.download = attachment.filename || `attachment_${attachmentIndex}`;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log(`✅ [ATTACHMENT-DOWNLOAD] Outbound download initiated: ${attachment.filename}`);
        } catch (err) {
          console.error('📎 [ATTACHMENT-DOWNLOAD] Storage download failed:', err);
          alert(`Failed to download attachment: ${err.message || 'Unknown error'}`);
        } finally {
          restoreIcon();
        }
        return;
      }

      // ── Inbound attachments (IMAP via cloud function) ──
      if (!email.uid) {
        console.error('📎 [ATTACHMENT-DOWNLOAD] Email UID not found');
        alert('Error: Email UID not found. Cannot download attachment.');
        restoreIcon();
        return;
      }

      // Cannot use a plain `<a href>` here — the cloud function requires a
      // Bearer token in the Authorization header (verifyAuthAndAccess). A
      // direct browser navigation strips that, so we fetch via
      // authenticatedFetch, materialize the bytes as a Blob, and trigger the
      // download from a temporary object URL.
      try {
        // Pass accountId so the backend opens the correct mailbox — picking
        // the first account (legacy behaviour) breaks for users with multiple
        // accounts where the email lives in a non-default INBOX.
        const params = new URLSearchParams({
          uid: userId,
          messageUid: String(email.uid),
          attachmentIndex: String(attachmentIndex),
        });
        if (email.accountId) params.set('accountId', String(email.accountId));
        const baseUrl = getCloudFunctionUrl('downloadEmailAttachment');
        const downloadUrl = `${baseUrl}?${params.toString()}`;
        const response = await authenticatedFetch(downloadUrl, { method: 'GET' });

        if (!response.ok) {
          let errorDetails = `${response.status} ${response.statusText}`;
          try {
            const errBody = await response.json();
            if (errBody?.error) errorDetails = errBody.error;
          } catch (_) {
            // Response wasn't JSON — keep the status text fallback.
          }
          throw new Error(errorDetails);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = attachment.filename || `attachment_${attachmentIndex}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Revoke shortly after click to give the browser time to consume it.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
        console.log(`✅ [ATTACHMENT-DOWNLOAD] IMAP download initiated: ${attachment.filename}`);
      } catch (downloadError) {
        console.error('📎 [ATTACHMENT-DOWNLOAD] Download failed:', downloadError);
        alert(`Failed to download attachment: ${downloadError.message || 'Unknown error'}`);
      } finally {
        restoreIcon();
      }
    } catch (error) {
      console.error('📎 [ATTACHMENT-DOWNLOAD] Unexpected error:', error);
      alert(`Error downloading attachment: ${error.message || 'Unknown error'}`);
      restoreIcon();
    }
  };

  // Funktion zum Speichern des Antwortstatus in der Datenbank
  const saveReplyStatus = async (emailId, saveToDatabase = true) => {
    try {
      // Lokalen Status aktualisieren
      setHasReplied(true);
      setIsReplied(true);
      
      console.log(`📧 [REPLY-STATUS] Setting reply status for email ${emailId}`);
      
      // ✅ NEU: Server-Status aktualisieren via onMarkAsReplied (Cloud Function)
      // Dies setzt isReplied: true direkt auf dem E-Mail-Dokument in Firestore
      if (onMarkAsReplied && typeof onMarkAsReplied === 'function') {
        onMarkAsReplied(email);
        console.log(`✅ [FIRESTORE] Reply status will be updated in Firestore via Cloud Function`);
      }
      
      return true;
    } catch (error) {
      console.error("Error saving reply status:", error);
      return false;
    }
  };

  // Callback für das Schließen des EmailAiPopup
  const handleEmailPopupClose = async (emailSent = false) => {
    // Schließe sofort das Popup
    setShowEmailAiPopup(false);
    
    // Prüfe explizit, ob eine E-Mail gesendet wurde (strikte Gleichheit)
    if (emailSent === true) {
      console.log("E-Mail sent successfully!");
      
      // Zeige die Erfolgsmeldung
      setEmailNotifier({
        status: 'success',
        message: 'E-Mail sent successfully!'
      });
      
      // Notifier nach 5 Sekunden automatisch ausblenden
      setTimeout(() => {
        resetEmailNotifier();
      }, 5000);
      
      // 🔥 FIX: Nur bei echten Antworten (nicht bei Forward) Reply-Status aktualisieren
      if (emailReplyData && !emailReplyData.isForwardOperation) {
        const emailId = getEmailId();
        await saveReplyStatus(emailId);
      } else {
        console.log("🔄 [FORWARD-INFO] Skipping reply status update for forward operation");
      }
    } else {
      console.log("EmailAiPopup wurde geschlossen ohne E-Mail zu senden");
    }
  };

  // Funktion zum Zurücksetzen des Notifiers
  const resetEmailNotifier = () => {
    setEmailNotifier({ 
      status: '', 
      message: '', 
      scheduledTime: null, 
      recipientEmail: null 
    });
  };

  // Funktion zum Behandeln des E-Mail-Ergebnisses - direkt vom EmailAiPopup aufgerufen
  const handleEmailResult = (status, message, rowData, scheduledTime, recipientEmail) => {
    console.log(`E-Mail-Ergebnis empfangen: ${status} - ${message}`, {
      scheduledTime: scheduledTime ? new Date(scheduledTime).toLocaleString() : null,
      recipientEmail: recipientEmail || 'nicht angegeben',
      isForward: rowData?.isForwardOperation || false
    });
    
    // Stelle sicher, dass scheduledTime ein Zeitstempel ist
    let validScheduledTime = scheduledTime;
    if (scheduledTime && typeof scheduledTime === 'string') {
      try {
        validScheduledTime = new Date(scheduledTime).getTime();
        if (isNaN(validScheduledTime)) {
          console.error("Ungültiger Zeitstempel:", scheduledTime);
          validScheduledTime = null;
        }
      } catch (e) {
        console.error("Fehler beim Konvertieren des Zeitstempels:", e);
        validScheduledTime = null;
      }
    }
    
    // 🔄 [FORWARD-INFO] Spezielle Behandlung für Forward-E-Mails
    if (rowData?.isForwardOperation && status === 'success') {
      console.log("🔄 [FORWARD-SUCCESS] Forward-E-Mail erfolgreich gesendet");
    }
    
    setEmailNotifier({ 
      status, 
      message, 
      scheduledTime: validScheduledTime,
      recipientEmail: recipientEmail || (rowData ? rowData.lead_email : null)
    });
    
    // Automatisch nach 5 Sekunden ausblenden
    setTimeout(() => {
      resetEmailNotifier();
    }, 5000);
  };

  // Funktion zur sicheren Anzeige des E-Mail-Inhalts mit React Letter
  const renderEmailContent = () => {
    // Wenn keine Inhalte verfügbar sind
    if (!email.bodyHtml && !email.bodyText && !email.content && !email.text && !email.htmlStorageUrl) {
      return <p>No content available</p>;
    }

    // Bestimme HTML und Text Content
    const htmlContent = email.bodyHtml || email.content || '';
    const textContent = email.bodyText || email.text || '';

    return (
      <div className="email-content-safe-container">
        {/* Action Buttons */}
        <div className="email-view-mode-toggle">
          <div className="email-view-toggle-info">
            Email
          </div>
          <div className="email-view-toggle-actions">
            {/* Blacklist Button - ganz links */}
            {!isSentEmail && (
              <button 
                className={`inbox-email-blacklist-action-btn ${blacklisting ? 'blacklisting' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleBlacklist();
                }}
                disabled={blacklisting}
                title="Add to Blacklist"
              >
                {blacklisting ? (
                  <span className="process_spinner"></span>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M4.93 4.93l14.14 14.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </button>
            )}
            
            {/* Forward Button */}
            <button 
              className="inbox-email-forward-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleForwardWithAi();
              }}
              title="Forward with AI"
            >
              <img src="/img/reply-message.svg" alt="Forward" className="forward_icon" style={{width: '16px', height: '16px', filter: 'brightness(0) saturate(100%) invert(28%) sepia(15%) saturate(1200%) hue-rotate(170deg) brightness(95%)', transition: 'filter 0.25s ease'}}/>
            </button>
            
            {/* Add To Leads Button - neben Reply */}
            {!isSentEmail && emailType !== 'safeguard' && (
              <button 
                className={`inbox-email-add-to-leads-btn ${addingToLeads ? 'adding' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddToLeadsModal(true);
                }}
                disabled={addingToLeads}
                title="Add to Leads"
              >
                {addingToLeads ? (
                  <>
                    Adding...
                    <span className="process_spinner"></span>
                  </>
                ) : (
                  <>
                    Lead
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginLeft: '4px'}}>
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="23" y1="11" x2="17" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </>
                )}
              </button>
            )}
            
            {/* Reply Button - ganz rechts (nur für empfangene E-Mails) */}
            {!isSentEmail && (
              emailType === 'safeguard' ? (
                // Send Manually Button für SafeGuard Drafts
                <button 
                  className="inbox-email-send-manually-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendManually();
                  }}
                  title="Edit and Send Manually"
                >
                  Send Manually
                  <img src="/img/edit.svg" alt="Edit" className="edit_icon" />
                </button>
              ) : (
                // Normaler Reply Button für andere E-Mail-Typen
                <button 
                  className="inbox-email-reply-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReplyWithAi();
                  }}
                  title="Reply with AI"
                >
                  {hasReplied ? 'Reply Again' : 'Reply Email'}
                  <span className="reply_icon_circle">
                    <img src="/img/paperplane.svg" alt="Reply" className="reply_icon" />
                  </span>
                </button>
              )
            )}
          </div>
        </div>

        {/* Email Content mit React Letter */}
        <div className="email-content-body">
          {htmlViewMode === 'iframe' && email.htmlStorageUrl ? (
            // Original iframe view (fallback)
            <iframe
              src={email.htmlStorageUrl}
              className="email-content-iframe"
              title="Email Content"
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              style={{
                width: '100%',
                minHeight: '400px',
                border: 'none',
                padding: '10px',
                borderRadius: '12px',
                backgroundColor: '#fff',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
            />
          ) : isSentEmail && htmlContent ? (
            // Eigene gesendete Mail → 1:1 wie versendet rendern (Footer-Bild lädt
            // direkt, kein App-CSS-Reset zerdrückt den Footer-Abstand).
            <SentMailFrame html={htmlContent} title={email.subject} />
          ) : (
            // Sichere React Letter Anzeige
            <div className="email-content-react-letter">
              <Letter 
                html={htmlContent}
                text={textContent}
                useIframe={false} // Wir verwenden kein iframe für bessere Integration
                iframeTitle={email.subject}
                rewriteExternalLinks={(url) => {
                  // Privacy-Protection: Externe Links über Proxy leiten
                  console.log('Rewriting external link:', url);
                  return `/api/proxy-link?url=${encodeURIComponent(url)}`;
                }}
                rewriteExternalResources={(url) => {
                  // Bilder und Ressourcen über Proxy laden
                  console.log('Rewriting external resource:', url);
                  return `/api/proxy-image?url=${encodeURIComponent(url)}`;
                }}
                allowedSchemas={['http', 'https', 'mailto']}
                preserveCssPriority={true}
                className="safe-email-content"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Rendere zusätzliche Informationen für gesendete E-Mails
  const renderSentEmailInfo = () => {
    if (!isSentEmail) return null;
    
    return (
      <div className="smart_email_sent_details">
        {email.delay && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Verzögerung:</span>
            <span className="smart_email_sent_detail_value">
              {email.delay} Minuten ({Math.round(email.delay / 60 * 10) / 10} Stunden)
            </span>
          </div>
        )}
        
        {email.randomizationFactor !== undefined && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Zufallsfaktor:</span>
            <span className="smart_email_sent_detail_value">
              {Math.round(email.randomizationFactor * 100)}%
            </span>
          </div>
        )}
        
        {email.scheduledTime && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Geplant für:</span>
            <span className="smart_email_sent_detail_value">
              {new Date(email.scheduledTime).toLocaleString()}
            </span>
          </div>
        )}
        
        {email.actualSendTime && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Tatsächlich gesendet:</span>
            <span className="smart_email_sent_detail_value">
              {new Date(email.actualSendTime).toLocaleString()}
            </span>
          </div>
        )}
        
        {email.emailType && (
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">E-Mail-Typ:</span>
            <span className="smart_email_sent_detail_value">
              {email.emailType}
            </span>
          </div>
        )}
      </div>
    );
  };

  // SafeGuard-Informationen für abgelehnte E-Mails anzeigen
  const renderGenerationErrorInfo = () => {
    if (emailType !== 'safeguard' || email.reason !== 'generation_error') return null;

    const sourceLabel = email.errorSource === 'outreach_job' ? 'Outreach Job'
      : email.errorSource === 'flow_automation' ? 'Flow Automation' : 'Auto-Send';

    return (
      <div className="sg-card">
        <div className="sg-card-header">
          <div className="sg-card-header-left">
            <div className="sg-shield-icon" style={{ color: '#ef4444' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <span className="sg-card-title">Generation Error</span>
          </div>
          <span className="sg-decision-badge rejected">{sourceLabel}</span>
        </div>

        {email.safeGuardInfo && (
          <div className="sg-analysis-block">
            <p className="sg-analysis-text">{email.safeGuardInfo}</p>
          </div>
        )}

        {email.safeGuardIssues?.length > 0 && (
          <div className="sg-list-section">
            <div className="sg-list-header sg-list-header-issues">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>Error Details</span>
            </div>
            <div className="sg-list-items">
              {email.safeGuardIssues.map((issue, i) => (
                <div key={i} className="sg-list-item sg-item-issue">{issue}</div>
              ))}
            </div>
          </div>
        )}

        {email.templateName && (
          <div className="sg-score-meta" style={{ marginTop: '8px' }}>
            <div className="sg-meta-row">
              <span className="sg-meta-label">Template</span>
              <span className="sg-meta-value">{email.templateName}</span>
            </div>
            {email.jobId && (
              <div className="sg-meta-row">
                <span className="sg-meta-label">Job ID</span>
                <span className="sg-meta-value" style={{ fontSize: '10px' }}>{email.jobId}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // CRM-Block-Card (vom Pre-Send-Guard abgewiesene Sends, z.B. Close-CRM
  // Konflikt). Wird genauso im SafeGuard-Tab gelistet wie SafeGuard- und
  // Scorer-Rejections, hat aber eigene Felder (`crmBlock`) und einen
  // dedizierten "Close"-Tag.
  const renderCrmBlockedInfo = () => {
    if (emailType !== 'safeguard') return null;
    if (email.reason !== 'crm_blocked') return null;

    const crm = email.crmBlock || {};
    const providerLabel = crm.provider === 'close' ? 'Close' : (crm.provider || 'CRM');
    const matchKindLabel = crm.matchKind === 'email' ? 'Exact email' : 'Domain';
    const sourceLabel = crm.leadSourceValue
      ? `Lead Source "${crm.leadSourceValue}"`
      : 'empty Lead Source';

    return (
      <div className="sg-card">
        <div className="sg-card-header">
          <div className="sg-card-header-left">
            <div className="sg-shield-icon" style={{ color: '#0d6efd' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </div>
            <span className="sg-card-title">CRM Guard Block</span>
          </div>
          <span
            className="sg-decision-badge rejected"
            style={{ background: '#0d6efd', color: '#fff' }}
            title={`Blocked by ${providerLabel} CRM guard`}
          >
            {providerLabel}
          </span>
        </div>

        {email.safeGuardInfo && (
          <div className="sg-analysis-block">
            <p className="sg-analysis-text">{email.safeGuardInfo}</p>
          </div>
        )}

        <div className="sg-score-meta" style={{ marginTop: '8px' }}>
          <div className="sg-meta-row">
            <span className="sg-meta-label">CRM</span>
            <span className="sg-meta-value">{providerLabel}</span>
          </div>
          <div className="sg-meta-row">
            <span className="sg-meta-label">Match</span>
            <span className="sg-meta-value">{matchKindLabel}</span>
          </div>
          <div className="sg-meta-row">
            <span className="sg-meta-label">Foreign Source</span>
            <span className="sg-meta-value">{sourceLabel}</span>
          </div>
          {crm.leadId && (
            <div className="sg-meta-row">
              <span className="sg-meta-label">Lead ID</span>
              <span className="sg-meta-value" style={{ fontSize: '10px' }}>{crm.leadId}</span>
            </div>
          )}
          {crm.status && (
            <div className="sg-meta-row">
              <span className="sg-meta-label">Status</span>
              <span className="sg-meta-value">{crm.status}</span>
            </div>
          )}
        </div>

        {Array.isArray(email.safeGuardImprovements) && email.safeGuardImprovements.length > 0 && (
          <div className="sg-list-section">
            <div className="sg-list-header sg-list-header-improve">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              <span>Next Steps</span>
            </div>
            <div className="sg-list-items">
              {email.safeGuardImprovements.map((item, i) => (
                <div key={i} className="sg-list-item sg-item-improve">{item}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Bounce-Reduction-Card: der Empfänger war eine Entscheider-Adresse, die im
  // letzten Schritt als endgültig ungültig erkannt wurde, und es gab keine
  // Standard-/Firmen-Mailbox (info@, kontakt@, …) zum Umschalten. Die Mail
  // wurde daher nicht versendet, sondern hier geparkt.
  const renderEmailInvalidInfo = () => {
    if (emailType !== 'safeguard') return null;
    if (email.reason !== 'email_invalid_final') return null;

    const v = email.emailValidationBlock || {};

    return (
      <div className="sg-card">
        <div className="sg-card-header">
          <div className="sg-card-header-left">
            <div className="sg-shield-icon" style={{ color: '#dc3545' }}>
              <i className="fa fa-ban" style={{ fontSize: '18px' }}></i>
            </div>
            <span className="sg-card-title">Invalid Email Blocked</span>
          </div>
          <span
            className="sg-decision-badge rejected"
            style={{ background: '#dc3545', color: '#fff' }}
            title="Recipient address finally invalid"
          >
            Bounce Reduction
          </span>
        </div>

        {email.safeGuardInfo && (
          <div className="sg-analysis-block">
            <p className="sg-analysis-text">{email.safeGuardInfo}</p>
          </div>
        )}

        <div className="sg-score-meta" style={{ marginTop: '8px' }}>
          <div className="sg-meta-row">
            <span className="sg-meta-label">Recipient</span>
            <span className="sg-meta-value">{v.recipientEmail || email.to || '—'}</span>
          </div>
          {v.validationStatus && (
            <div className="sg-meta-row">
              <span className="sg-meta-label">Status</span>
              <span className="sg-meta-value">{v.validationStatus}</span>
            </div>
          )}
          {v.validationReason && (
            <div className="sg-meta-row">
              <span className="sg-meta-label">Reason</span>
              <span className="sg-meta-value">{v.validationReason}</span>
            </div>
          )}
        </div>

        {Array.isArray(email.safeGuardImprovements) && email.safeGuardImprovements.length > 0 && (
          <div className="sg-list-section">
            <div className="sg-list-header sg-list-header-improve">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              <span>Next Steps</span>
            </div>
            <div className="sg-list-items">
              {email.safeGuardImprovements.map((item, i) => (
                <div key={i} className="sg-list-item sg-item-improve">{item}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Reply-Intercept-Card (Follow-up vom Reply-Guard abgefangen, weil der
  // Kontakt – oder dessen Domain, außer Freemail – in der Zwischenzeit
  // geantwortet hat). Eigene Card wie der CRM-Block, mit dediziertem
  // "Reply erkannt"-Tag. Kein Score-Ring (Rating=0/10 wäre irreführend).
  const renderReplyInterceptedInfo = () => {
    if (emailType !== 'safeguard') return null;
    if (email.reason !== 'reply_received') return null;

    const recipient = email.originalTo || email.to || email.recipientEmail || '';

    return (
      <div className="sg-card">
        <div className="sg-card-header">
          <div className="sg-card-header-left">
            <div className="sg-shield-icon" style={{ color: '#16a34a' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 17 4 12 9 7"/>
                <path d="M20 18v-2a4 4 0 0 0-4-4H4"/>
              </svg>
            </div>
            <span className="sg-card-title">Follow-up abgefangen</span>
          </div>
          <span
            className="sg-decision-badge rejected"
            style={{ background: '#16a34a', color: '#fff' }}
            title="Kontakt hat bereits geantwortet — Follow-up wurde nicht gesendet"
          >
            Reply erkannt
          </span>
        </div>

        {email.safeGuardInfo && (
          <div className="sg-analysis-block">
            <p className="sg-analysis-text">{email.safeGuardInfo}</p>
          </div>
        )}

        <div className="sg-score-meta" style={{ marginTop: '8px' }}>
          {recipient && (
            <div className="sg-meta-row">
              <span className="sg-meta-label">Empfänger</span>
              <span className="sg-meta-value">{recipient}</span>
            </div>
          )}
          {email.businessName && (
            <div className="sg-meta-row">
              <span className="sg-meta-label">Kontakt</span>
              <span className="sg-meta-value">{email.businessName}</span>
            </div>
          )}
          {email.emailType && (
            <div className="sg-meta-row">
              <span className="sg-meta-label">Stufe</span>
              <span className="sg-meta-value">{email.emailType}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Erkennt, ob ein SafeGuard-Draft NICHT wegen schlechter Inhalte geparkt
  // wurde, sondern weil SafeGuard die E-Mail technisch nicht bewerten konnte
  // (Timeout des LLM-Calls / Service-Fehler). Neuere Drafts tragen dafür ein
  // explizites Flag (safeGuardServiceError); ältere Drafts werden anhand der
  // Fehlertexte heuristisch erkannt, damit auch bereits geparkte Mails den
  // echten Grund anzeigen.
  const isSafeGuardServiceError = () => {
    if (emailType !== 'safeguard') return false;
    if (email.reason !== 'safeguard_rejection') return false;
    if (email.safeGuardServiceError === true) return true;
    const info = (email.safeGuardInfo || '').toLowerCase();
    if (info.includes('safeguard call failed') || info.includes('safeguard evaluation failed')) return true;
    const issues = Array.isArray(email.safeGuardIssues) ? email.safeGuardIssues : [];
    return issues.some((i) => /safeguard (service|evaluation) error/i.test(String(i)));
  };

  // Dedizierte Karte für SafeGuard-Service-Fehler/Timeouts. Macht im Frontend
  // explizit klar, WARUM die E-Mail nicht gesendet wurde: SafeGuard konnte sie
  // nicht prüfen (z.B. 30s-Timeout), also wurde sie sicherheitshalber geparkt.
  // Das ist KEIN inhaltliches Qualitätsproblem — daher kein irreführendes
  // Score-Ring (1/10), sondern eine klare Fehlermeldung + Handlungsempfehlung.
  const renderSafeGuardServiceErrorInfo = () => {
    if (!isSafeGuardServiceError()) return null;

    const recipient = email.originalTo || email.to || email.recipientEmail || '';
    const errorMessage = email.safeGuardErrorMessage
      || email.safeGuardInfo
      || 'SafeGuard konnte die E-Mail nicht bewerten.';
    const isTimeout = /timeout/i.test(errorMessage);

    return (
      <div className="sg-card">
        <div className="sg-card-header">
          <div className="sg-card-header-left">
            <div className="sg-shield-icon" style={{ color: '#f59e0b' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <span className="sg-card-title">SafeGuard nicht erreichbar</span>
          </div>
          <span
            className="sg-decision-badge rejected"
            style={{ background: '#f59e0b', color: '#fff' }}
            title="SafeGuard konnte die E-Mail nicht bewerten — Versand sicherheitshalber gestoppt"
          >
            {isTimeout ? 'Timeout' : 'Service-Fehler'}
          </span>
        </div>

        <div className="sg-analysis-block">
          <p className="sg-analysis-text">
            Diese E-Mail wurde <strong>nicht gesendet</strong>, weil SafeGuard sie nicht
            rechtzeitig bewerten konnte{isTimeout ? ' (Zeitüberschreitung der Bewertung)' : ''}.
            Das ist <strong>kein inhaltliches Qualitätsproblem</strong> — die Mail wurde nur
            sicherheitshalber hier geparkt. Du kannst sie unverändert erneut senden bzw. manuell
            verschicken.
          </p>
        </div>

        <div className="sg-list-section">
          <div className="sg-list-header sg-list-header-issues">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span>Fehlerdetails</span>
          </div>
          <div className="sg-list-items">
            <div className="sg-list-item sg-item-issue">{errorMessage}</div>
          </div>
        </div>

        <div className="sg-score-meta" style={{ marginTop: '8px' }}>
          {recipient && (
            <div className="sg-meta-row">
              <span className="sg-meta-label">Empfänger</span>
              <span className="sg-meta-value">{recipient}</span>
            </div>
          )}
          {email.emailType && (
            <div className="sg-meta-row">
              <span className="sg-meta-label">Typ</span>
              <span className="sg-meta-value">{email.emailType}</span>
            </div>
          )}
        </div>

        <div className="sg-list-section">
          <div className="sg-list-header sg-list-header-improve">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>Nächste Schritte</span>
          </div>
          <div className="sg-list-items">
            <div className="sg-list-item sg-item-improve">E-Mail unverändert erneut senden — SafeGuard versucht die Bewertung dann erneut.</div>
            <div className="sg-list-item sg-item-improve">Tritt das öfter auf, ist die SafeGuard-Bewertung gerade zu langsam (Timeout). Dann ggf. das Call-Timeout serverseitig erhöhen.</div>
          </div>
        </div>
      </div>
    );
  };

  const renderSafeGuardInfo = () => {
    if (emailType !== 'safeguard') return null;
    if (email.reason === 'generation_error') return null;
    // Service-Fehler/Timeouts haben eine eigene Karte (siehe oben). Das
    // generische Score-Ring-UI (Rating 1/10) wäre hier irreführend, weil es
    // wie eine inhaltliche Ablehnung aussieht.
    if (isSafeGuardServiceError()) return null;
    // CRM-Blocks haben eigene Card oben — die generische SafeGuard-Card mit
    // Score-Ring etc. wäre hier irreführend (Score=0, Rating=0/10).
    if (email.reason === 'crm_blocked') return null;
    // Reply-Intercepts haben ebenfalls eine eigene Card (siehe oben).
    if (email.reason === 'reply_received') return null;
    // Bounce-Reduction-Blocks haben ihre eigene Card (siehe oben).
    if (email.reason === 'email_invalid_final') return null;

    const hasSafeGuardInfo = email.safeGuardRating !== undefined ||
                             email.safeGuardInfo ||
                             email.safeGuardIssues?.length > 0 ||
                             email.safeGuardImprovements?.length > 0;

    if (!hasSafeGuardInfo) return null;

    const rating = email.safeGuardRating || 0;
    const minimum = email.minimumRating || 6;
    const pct = Math.round((rating / 10) * 100);
    const isApproved = email.safeGuardDecision;

    const getRatingTier = (r) => {
      if (r >= 7) return 'good';
      if (r >= 4) return 'warn';
      return 'bad';
    };
    const tier = getRatingTier(rating);

    return (
      <div className="sg-card">
        {/* Header */}
        <div className="sg-card-header">
          <div className="sg-card-header-left">
            <div className="sg-shield-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </div>
            <span className="sg-card-title">SafeGuard Analysis</span>
          </div>
          <span className={`sg-decision-badge ${isApproved ? 'approved' : 'rejected'}`}>
            {isApproved ? 'Approved' : 'Rejected'}
          </span>
        </div>

        {/* Score ring + stats */}
        <div className="sg-score-section">
          <div className="sg-score-ring-wrap">
            <svg className="sg-score-ring" viewBox="0 0 80 80">
              <circle className="sg-score-ring-bg" cx="40" cy="40" r="34" />
              <circle
                className={`sg-score-ring-fill sg-tier-${tier}`}
                cx="40" cy="40" r="34"
                strokeDasharray={`${pct * 2.136} 213.6`}
                strokeDashoffset="0"
              />
            </svg>
            <div className="sg-score-value">
              <span className={`sg-score-num sg-tier-${tier}`}>{rating}</span>
              <span className="sg-score-max">/10</span>
            </div>
          </div>
          <div className="sg-score-meta">
            <div className="sg-meta-row">
              <span className="sg-meta-label">Minimum Required</span>
              <span className="sg-meta-value">{minimum}/10</span>
            </div>
            <div className="sg-meta-row">
              <span className="sg-meta-label">Issues</span>
              <span className="sg-meta-value">{email.safeGuardIssues?.length || 0}</span>
            </div>
            <div className="sg-meta-row">
              <span className="sg-meta-label">Suggestions</span>
              <span className="sg-meta-value">{email.safeGuardImprovements?.length || 0}</span>
            </div>
          </div>
        </div>

        {/* Analysis text */}
        {email.safeGuardInfo && (
          <div className="sg-analysis-block">
            <p className="sg-analysis-text">{email.safeGuardInfo}</p>
          </div>
        )}

        {/* Issues */}
        {email.safeGuardIssues?.length > 0 && (
          <div className="sg-list-section">
            <div className="sg-list-header sg-list-header-issues">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>Issues Found</span>
            </div>
            <div className="sg-list-items">
              {email.safeGuardIssues.map((issue, i) => (
                <div key={i} className="sg-list-item sg-item-issue">{issue}</div>
              ))}
            </div>
          </div>
        )}

        {/* Improvements */}
        {email.safeGuardImprovements?.length > 0 && (
          <div className="sg-list-section">
            <div className="sg-list-header sg-list-header-improve">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
              <span>Suggestions</span>
            </div>
            <div className="sg-list-items">
              {email.safeGuardImprovements.map((imp, i) => (
                <div key={i} className="sg-list-item sg-item-improve">{imp}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPreSendScoreInfo = () => {
    const score = email.preSendScore;
    if (!score || emailType !== 'safeguard') return null;

    const getScoreColor = (val) => {
      if (val >= 7) return '#22c55e';
      if (val >= 4) return '#f59e0b';
      return '#ef4444';
    };

    const getRiskColor = (val) => {
      if (val <= 3) return '#22c55e';
      if (val <= 6) return '#f59e0b';
      return '#ef4444';
    };

    const REASON_LABELS = {
      spam_words: 'Spam-Woerter erkannt',
      too_many_links: 'Zu viele Links',
      hyperbole: 'Uebertriebene Sprache',
      no_clear_value: 'Kein klarer Mehrwert',
      too_short: 'Text zu kurz',
      too_long: 'Text zu lang',
      missing_personalization: 'Keine Personalisierung',
      aggressive_cta: 'Aggressiver Call-to-Action',
      all_caps: 'Grossbuchstaben-Spam',
      excessive_punctuation: 'Zu viele Satzzeichen',
    };

    return (
      <div className="smart_email_safeguard_details" style={{ borderLeft: '3px solid #7c3aed' }}>
        <div className="smart_email_safeguard_details_title">
          📊 Pre-Send Score Analysis
        </div>

        <div className="smart_email_safeguard_rating_section">
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Overall Score:</span>
            <span className="smart_email_safeguard_rating_value" style={{ color: getScoreColor(score.compositeScore), fontWeight: 'bold' }}>
              {score.compositeScore}/10
            </span>
          </div>
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Buyer Value:</span>
            <span style={{ color: getScoreColor(score.buyerValue), fontWeight: '600' }}>
              {score.buyerValue}/10
            </span>
          </div>
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Deliverability Risk:</span>
            <span style={{ color: getRiskColor(score.deliverabilityRisk), fontWeight: '600' }}>
              {score.deliverabilityRisk}/10
            </span>
          </div>
          <div className="smart_email_sent_detail_item">
            <span className="smart_email_sent_detail_label">Recommendation:</span>
            <span className={`smart_email_safeguard_decision ${score.recommendation === 'send' ? 'approved' : 'rejected'}`}>
              {score.recommendation === 'send' ? '✅ Send' : score.recommendation === 'revise' ? '✏️ Revise' : '🚫 Block'}
            </span>
          </div>
        </div>

        {score.reasonTags?.length > 0 && (
          <div className="smart_email_safeguard_issues">
            <div className="smart_email_safeguard_section_title">Issues Detected:</div>
            <ul className="smart_email_safeguard_list">
              {score.reasonTags.map((tag, i) => (
                <li key={i} className="smart_email_safeguard_issue">
                  ⚠️ {REASON_LABELS[tag] || tag}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Füge eine Funktion zum Rendern des Antwort-Status-Indikators hinzu
  const renderReplyStatus = () => {
    if (isSentEmail) return null; // Kein Antwort-Status für gesendete E-Mails
    
    const replied = isReplied || hasReplied || email.isReplied;
    
    if (!replied) return null;
    
    return (
      <div className="email_replied_indicator" title="Replied">
        <img src="/img/reply-message.svg" alt="Replied" className="replied_icon" />
      </div>
    );
  };

  // Stelle sicher, dass wir die richtige CSS-Klasse basierend auf dem Lese-Status und Spam-Status haben
  const emailClassName = `smartInbox_email ${expanded ? 'expanded' : ''} ${
    !isRead ? 'unread' : ''
  } ${isSpam ? 'spam' : ''} ${isReplied || hasReplied || email.isReplied ? 'replied' : ''} ${isSentEmail ? 'sent' : ''}`;

  // Render-Methode für das EmailAiPopup als Portal
  const renderEmailPopup = () => {
    if (showEmailAiPopup && emailReplyData) {
      return ReactDOM.createPortal(
        <div className="popupOverlayWrapper">
          <div className="popupOverlayBackground"></div>
          <div className="popupContentWrapper">
            <EmailAiPopup 
              onClose={(emailSent) => handleEmailPopupClose(emailSent)} 
              rowData={emailReplyData} 
              userId={userId}
              isReply={true}
              source="inbox"
              onEmailResult={handleEmailResult}
            />
          </div>
        </div>,
        document.body // Rendere direkt im body-Element
      );
    }
    return null;
  };

  // Funktion zum Verarbeiten mit Agents
  const handleProcessWithAgents = async () => {
    if (!onProcessWithAgents || processingWithAgents) return;
    
    // Prüfe ob Email auf Blacklist ist
    const extractEmailAddress = (fromString) => {
      if (!fromString) return '';
      const emailMatch = fromString.match(/<([^>]+)>/) || fromString.match(/([^\s@]+@[^\s@]+\.[^\s@]+)/);
      return emailMatch ? (emailMatch[1] || emailMatch[0]).trim().toLowerCase() : '';
    };
    
    const emailAddress = extractEmailAddress(email.from);
    if (emailAddress) {
      try {
        const blacklistDocRef = doc(firestore, `World/${userId}/blacklist/${emailAddress}`);
        const blacklistDoc = await getDoc(blacklistDocRef);
        
        if (blacklistDoc.exists()) {
          console.log(`🚫 [BLACKLIST] Cannot auto-reply - email is blacklisted: ${emailAddress}`);
          setError(`❌ Cannot process "${emailAddress}" - this email is on your blacklist.`);
          return;
        }
      } catch (error) {
        console.error('Error checking blacklist:', error);
      }
    }
    
    const emailId = getEmailId();
    if (!emailId) return;

    setProcessingWithAgents(true);
    try {
      await onProcessWithAgents(emailId, email.isSpam || false);
    } catch (error) {
      console.error('Error processing with agents:', error);
    } finally {
      setProcessingWithAgents(false);
    }
  };

  // Blacklist-Funktion: Custom domains → Domain-Level block; Consumer emails → Email-Level block.
  // Always sets the not_qualified tag so the contact is visually struck through in the World DB table.
  const handleBlacklist = async () => {
    if (blacklisting || isSentEmail) return;
    
    setBlacklisting(true);
    setError('');
    
    try {
      const extractEmailAddress = (fromString) => {
        if (!fromString) return '';
        const emailMatch = fromString.match(/<([^>]+)>/) || fromString.match(/([^\s@]+@[^\s@]+\.[^\s@]+)/);
        return emailMatch ? (emailMatch[1] || emailMatch[0]).trim().toLowerCase() : '';
      };
      
      const emailAddress = extractEmailAddress(email.from);
      
      if (!emailAddress) {
        setError('❌ No valid email address found in this email.');
        setBlacklisting(false);
        return;
      }
      
      // Determine domain and whether it's a consumer provider
      const atIdx = emailAddress.indexOf('@');
      const domain = atIdx !== -1 ? emailAddress.substring(atIdx).toLowerCase() : '';

      if (!domain) {
        setError('❌ Could not extract domain from email address.');
        setBlacklisting(false);
        return;
      }

      const isConsumerProvider = CONSUMER_EMAIL_PROVIDERS.includes(domain);
      const blockEntry = isConsumerProvider ? emailAddress : domain;
      const blockType = isConsumerProvider ? 'email' : 'domain';

      console.log(`🚫 [BLACKLIST] Starting blacklist process for: ${emailAddress} (${blockType}: ${blockEntry})`);
      
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      let contactId = null;
      let businessName = 'Unknown';
      let successMessage = '';
      const now = Date.now();

      const blacklistUpdateData = {
        lead_contacted: 'Blacklisted',
        lead_status: 'Blacklisted',
        blacklisted_at: new Date().toISOString(),
        globalOptOut: true,
        globalOptOutAt: now,
        globalOptOutSource: 'smart_inbox_blacklist',
        updated_at: now,
        [`tags.${TAG_IDS.NOT_QUALIFIED}`]: {
          id: TAG_IDS.NOT_QUALIFIED,
          name: 'Not Qualified',
          color: '#6b7280',
          icon: 'fa-times-circle',
          addedAt: now,
          addedBy: 'smart_inbox_blacklist',
        },
      };

      if (!isConsumerProvider) {
        // Custom domain: block ALL contacts sharing this domain
        const domainSuffix = domain; // e.g. "@stephaniehaupt.com"
        const allDocsSnapshot = await getDocs(dbCollectionRef);
        let domainContactCount = 0;

        const updatePromises = [];
        allDocsSnapshot.forEach((contactDoc) => {
          const data = contactDoc.data();
          const leadEmail = (data.lead_email || '').toLowerCase();
          if (leadEmail.endsWith(domainSuffix)) {
            domainContactCount++;
            if (!contactId) {
              contactId = contactDoc.id;
              let dmName = '';
              try {
                if (data.lead_decision_makers) {
                  const dms = typeof data.lead_decision_makers === 'string' ? JSON.parse(data.lead_decision_makers) : data.lead_decision_makers;
                  if (dms.length > 0 && dms[0].name) dmName = dms[0].name;
                }
              } catch (e) {}
              businessName = data.business_name || dmName || data.lead_firstname || 'Unknown';
            }
            const contactRef = doc(firestore, `World/${userId}/db/${contactDoc.id}`);
            updatePromises.push(updateDoc(contactRef, blacklistUpdateData));
          }
        });

        if (updatePromises.length > 0) {
          await Promise.all(updatePromises);
          console.log(`✅ [BLACKLIST] ${domainContactCount} contact(s) with domain "${domainSuffix}" blacklisted`);
          successMessage = `✅ Domain "${domainSuffix}" blocked — ${domainContactCount} contact${domainContactCount > 1 ? 's' : ''} marked as Not Qualified.`;
        } else {
          console.log(`⚠️ [BLACKLIST] No contacts found with domain "${domainSuffix}" — adding domain to blacklist only`);
          successMessage = `✅ Domain "${domainSuffix}" has been added to blacklist (no contacts found).`;
        }
      } else {
        // Consumer provider: block only the individual email address
        const emailQuery = query(dbCollectionRef, where('lead_email', '==', emailAddress));
        const querySnapshot = await getDocs(emailQuery);

        if (!querySnapshot.empty) {
          const contactDoc = querySnapshot.docs[0];
          contactId = contactDoc.id;
          const contactData = contactDoc.data();
          let dmName = '';
          try {
            if (contactData.lead_decision_makers) {
              const dms = typeof contactData.lead_decision_makers === 'string' ? JSON.parse(contactData.lead_decision_makers) : contactData.lead_decision_makers;
              if (dms.length > 0 && dms[0].name) dmName = dms[0].name;
            }
          } catch (e) {}
          businessName = contactData.business_name || dmName || contactData.lead_firstname || 'Unknown';

          console.log(`🚫 [BLACKLIST] Contact found: ${businessName} (${emailAddress})`);

          const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
          await updateDoc(contactRef, blacklistUpdateData);

          console.log(`✅ [BLACKLIST] Contact status + not_qualified tag updated`);
          successMessage = `✅ "${emailAddress}" blocked & contact marked as Not Qualified.`;
        } else {
          console.log(`⚠️ [BLACKLIST] Contact not found in database: ${emailAddress} — adding to blacklist only`);
          successMessage = `✅ "${emailAddress}" has been added to blacklist (contact not in database).`;
        }
      }
      
      // Add to blacklist collection (domain or email)
      const blacklistDocRef = doc(firestore, `World/${userId}/blacklist/${blockEntry}`);
      await setDoc(blacklistDocRef, {
        email: blockEntry,
        addedAt: now,
        source: 'smart_inbox',
        type: blockType,
        contactId: contactId,
        businessName: businessName,
        reason: contactId 
          ? 'Manually blacklisted from Smart Inbox' 
          : 'Blacklisted from Smart Inbox (contact not in database)',
      }, { merge: true });
      
      console.log(`✅ [BLACKLIST] ${blockType} "${blockEntry}" added to Firestore blacklist`);
      
      setEmailNotifier({
        status: 'blacklisted',
        message: successMessage
      });
      
    } catch (error) {
      console.error('🚫 [BLACKLIST] Error blacklisting email:', error);
      setError(`❌ Error blacklisting email: ${error.message}`);
    } finally {
      setBlacklisting(false);
    }
  };

  // Add To Leads Funktion - setzt lead_contacted auf "Lead" + Touchpoints
  const handleAddToLeads = async (touchpointsToAdd = {}) => {
    if (addingToLeads || isSentEmail) return;
    
    setAddingToLeads(true);
    setError('');
    setShowAddToLeadsModal(false);
    
    try {
      // Extrahiere Email-Adresse aus dem Email-Objekt
      const extractEmailAddress = (fromString) => {
        if (!fromString) return '';
        const emailMatch = fromString.match(/<([^>]+)>/) || fromString.match(/([^\s@]+@[^\s@]+\.[^\s@]+)/);
        return emailMatch ? (emailMatch[1] || emailMatch[0]).trim().toLowerCase() : '';
      };
      
      const emailAddress = extractEmailAddress(email.from);
      
      if (!emailAddress) {
        setError('❌ No valid email address found in this email.');
        setAddingToLeads(false);
        return;
      }
      
      console.log(`📋 [ADD-TO-LEADS] Adding to leads: ${emailAddress}`);
      console.log(`📋 [ADD-TO-LEADS] With touchpoints:`, touchpointsToAdd);
      
      // Suche nach dem Contact in Firestore db
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      const emailQuery = query(dbCollectionRef, where('lead_email', '==', emailAddress));
      const querySnapshot = await getDocs(emailQuery);
      
      if (!querySnapshot.empty) {
        // Contact gefunden - Update Status auf "Lead"
        const contactDoc = querySnapshot.docs[0];
        const contactId = contactDoc.id;
        const contactData = contactDoc.data();
        let dmNameForLeads = '';
        try {
          if (contactData.lead_decision_makers) {
            const dms = typeof contactData.lead_decision_makers === 'string' ? JSON.parse(contactData.lead_decision_makers) : contactData.lead_decision_makers;
            if (dms.length > 0 && dms[0].name) dmNameForLeads = dms[0].name;
          }
        } catch (e) {}
        const businessName = contactData.business_name || dmNameForLeads || contactData.lead_firstname || 'Unknown';

        console.log(`📋 [ADD-TO-LEADS] Contact found: ${businessName} (${emailAddress})`);
        
        // Merge existing touchpoints with new ones
        const existingTouchpoints = contactData.touchpoints || {};
        const mergedTouchpoints = { ...existingTouchpoints, ...touchpointsToAdd };
        
        // Update Contact Status in Firestore
        const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
        const now = Date.now();
        await updateDoc(contactRef, {
          lead_contacted: 'Lead',
          lead_status: 'Lead',
          lead_added_at: new Date().toISOString(),
          lead_since: now, // Numerischer Timestamp für Lead-Tracking
          updated_at: now,
          ...(Object.keys(mergedTouchpoints).length > 0 && { touchpoints: mergedTouchpoints })
        });
        
        console.log(`✅ [ADD-TO-LEADS] Contact status updated to "Lead"`);
        
        // Zeige Erfolgs-Feedback
        const touchpointCount = Object.keys(touchpointsToAdd).length;
        setEmailNotifier({
          status: 'lead_added',
          message: `"${businessName}" added to Leads${touchpointCount > 0 ? ` with ${touchpointCount} touchpoint${touchpointCount > 1 ? 's' : ''}` : ''}!`
        });
      } else {
        // Contact nicht gefunden - erstelle neuen Eintrag
        console.log(`📋 [ADD-TO-LEADS] Contact not found, creating new entry for: ${emailAddress}`);
        
        // Extrahiere Namen aus der Email
        const senderName = displayPerson.name || 'Unknown';
        
        // Erstelle neuen Kontakt in der DB
        const newContactRef = doc(collection(firestore, `World/${userId}/db`));
        const now = Date.now();
        await setDoc(newContactRef, {
          lead_email: emailAddress,
          business_name: senderName,
          lead_contacted: 'Lead',
          lead_status: 'Lead',
          lead_added_at: new Date().toISOString(),
          lead_since: now, // Numerischer Timestamp für Lead-Tracking
          timestamp: new Date().toISOString(),
          created_at: now,
          updated_at: now,
          source: 'smart_inbox',
          has_email: true,
          ...(Object.keys(touchpointsToAdd).length > 0 && { touchpoints: touchpointsToAdd })
        });
        
        console.log(`✅ [ADD-TO-LEADS] New contact created with status "Lead"`);
        
        const touchpointCount = Object.keys(touchpointsToAdd).length;
        setEmailNotifier({
          status: 'lead_added',
          message: `"${senderName}" added as new Lead${touchpointCount > 0 ? ` with ${touchpointCount} touchpoint${touchpointCount > 1 ? 's' : ''}` : ''}!`
        });
      }
      
      // Reset selected touchpoints
      setSelectedTouchpoints({});
      
    } catch (error) {
      console.error('📋 [ADD-TO-LEADS] Error adding to leads:', error);
      setError(`❌ Error adding to leads: ${error.message}`);
    } finally {
      setAddingToLeads(false);
    }
  };

  // Toggle Touchpoint Auswahl
  const toggleTouchpoint = (key, config) => {
    setSelectedTouchpoints(prev => {
      if (prev[key]) {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      } else {
        return { ...prev, [key]: config.hasCount ? 1 : true };
      }
    });
  };

  // Tag-Management Funktionen
  const handleSetTagClick = (e) => {
    e.stopPropagation();
    setShowSetTagPopup(true);
  };

  const handleSetTagPopupClose = () => {
    setShowSetTagPopup(false);
  };

  const handleTagsUpdated = (emailId, updatedTags) => {
    // Aktualisiere die lokalen Tags
    if (email) {
      email.tags = updatedTags;
    }
    console.log(`Tags updated for email ${emailId}:`, updatedTags);
    
    // Force Re-Render der Komponente um Tag-Änderungen sofort anzuzeigen
    setTagUpdateTrigger(prev => prev + 1);
  };

  // Prüfe, ob E-Mail Tags hat
  const hasEmailTags = () => {
    if (!email.tags) return false;
    
    const tagsArray = Array.isArray(email.tags) 
      ? email.tags 
      : Object.values(email.tags || {});
    
    return tagsArray.length > 0;
  };

  // Hol das erste Tag-Icon für den Button
  const getFirstTagIcon = () => {
    if (!hasEmailTags()) return null;
    
    const tagsArray = Array.isArray(email.tags) 
      ? email.tags 
      : Object.values(email.tags || {});
    
    const firstTag = tagsArray[0];
    if (typeof firstTag === 'string') return null;
    
    return firstTag.icon || null;
  };

  return (
    <div 
      className={`smartInbox_email ${isRead ? '' : 'unread'} ${expanded ? 'expanded' : ''} ${isReplied ? 'replied' : ''} ${isSentEmail ? 'sent' : ''} ${emailType === 'replied' ? 'replied' : ''} ${emailType === 'forwarded' ? 'forwarded' : ''}`}
    >
      <div className="smartInbox_email_header" onClick={toggleExpand}>
      
        <div className="smartInbox_email_left">
        {(emailType === 'sent' || emailType === 'replied' || emailType === 'forwarded') && (
          <div className="smartInbox_email_direction_container">
            <div className={`smartInbox_email_direction smartInbox_email_direction_${emailType}`}>
              {emailType === 'sent' && 'Sent to:'}
              {emailType === 'replied' && 'Replied to:'}
              {emailType === 'forwarded' && 'Forwarded to:'}
            </div>
            {emailType === 'sent' && email.emailType && (
              <div className={`smartInbox_email_type_tag ${email.emailType || 'default'}`}>
                {email.emailType}
              </div>
            )}
            {/* Status Anzeige */}
            {(emailType === 'sent' || emailType === 'replied' || emailType === 'forwarded') && (
              <div className={`smartInbox_email_status_tag smartInbox_email_status_${emailType}`}>
                {emailType === 'forwarded' ? (
                  // Für Forwarded: zeige "sent" Status (true/false)
                  email.sent ? 'Sent' : 'Not Sent'
                ) : (
                  // Für Sent und Replied: zeige "status" Wert
                  email.status || 'Unknown'
                )}
              </div>
            )}
          </div>
        )}
          {displayPerson.hasName && displayPerson.email && displayPerson.name !== displayPerson.email && (
            <span className="smartInbox_email_address" title={displayPerson.email}>
              {displayPerson.email}
            </span>
          )}
          <div className="smartInbox_email_sender">
            <span className="smartInbox_email_name">
              {displayPerson.name || 'Unknown'}
            </span>
            
            {/* Zeige Begeisterungsgrad an, wenn vorhanden und nicht gesendete E-Mail */}
            {!isSentEmail && enthusiasmInfo.score !== null && (
              <span className={`enthusiasm_score ${enthusiasmInfo.className}`} title="Enthusiasm Score">
                {enthusiasmInfo.text}
              </span>
            )}

            {!isSentEmail && email.replySubtype && email.replySubtype !== 'neutral' && (
              <span className={`reply_subtype reply_subtype_${email.replySubtype}`} title="Reply Type">
                {{
                  interested: 'Interesse',
                  price_objection: 'Preis-Einwand',
                  no_need: 'Kein Bedarf',
                  later: 'Später',
                  wrong_person: 'Falsche Person',
                  hostile: 'Feindlich',
                  spam_accusation: 'Spam-Beschwerde',
                }[email.replySubtype] || email.replySubtype}
              </span>
            )}

            {/* Opt-Out suggestion for negative replies / hostile / spam accusation */}
            {!isSentEmail && enthusiasmInfo.score !== null && !blacklisting && (
              enthusiasmInfo.score <= 2 || email.replySubtype === 'hostile' || email.replySubtype === 'spam_accusation'
            ) && (
              <button 
                className="inbox-email-optout-suggestion-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBlacklist();
                }}
                title="Strongly negative reply — block this contact?"
              >
                Block Contact
              </button>
            )}
            
            {/* Zeige E-Mail-Topic an, wenn vorhanden und nicht gesendete E-Mail */}
            {!isSentEmail && email.emailTopic && (
              <span className="email_topic" title="Email Topic">
                {email.emailTopic}
              </span>
            )}

            {/* Reply-Guard: Follow-up abgefangen, weil Kontakt geantwortet hat */}
            {emailType === 'safeguard' && email.reason === 'reply_received' && (
              <span
                className="smartInbox_email_reply_intercept_tag"
                title="Follow-up abgefangen — Kontakt hat bereits geantwortet"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: '#16a34a',
                  color: '#ffffff',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '10px',
                  marginLeft: '6px',
                }}
              >
                Reply erkannt
              </span>
            )}
            
            {/* Zeige Tags an, wenn vorhanden */}
            {email.tags && (
              <div className="smartInbox_email_tags">
                {(() => {
                  // Konvertiere Tags zu Array falls sie als Object gespeichert sind
                  const tagsArray = Array.isArray(email.tags) 
                    ? email.tags 
                    : Object.values(email.tags || {});
                  
                  return tagsArray.map((tag, index) => {
                    // Unterstütze sowohl neue Object-Struktur als auch alte Array-Struktur
                    const tagData = typeof tag === 'string' 
                      ? { tag: tag, name: tag } 
                      : tag;
                    
                    return (
                      <span 
                        key={index} 
                        className="smartInbox_email_tag"
                        style={{
                          backgroundColor: tagData.color || '#666',
                          color: '#ffffff' // Weiße Schrift wie gewünscht
                        }}
                      >
                        {tagData.icon && (
                          <img 
                            src={tagData.icon} 
                            alt=""
                            className="email_tag_icon"
                          />
                        )}
                        <span>{tagData.tag || tagData.name}</span>
                      </span>
                    );
                  });
                })()}
              </div>
            )}
            
            {/* Zeige "Spam"-Indikator an, wenn es sich um Spam handelt */}
            {isSpam && (
              <span className="smartInbox_email_spam_indicator">
                Spam
              </span>
            )}
            
            {/* Beantwortet-Status */}
            {renderReplyStatus()}
            
            {/* Original-Ansicht verfügbar Indikator */}
            {email.htmlStorageUrl && (
              <span className="email_original_view_indicator" title="Original view available">
              </span>
            )}
          </div>
          <div className="smartInbox_email_subject">
            {email.subject || '(No Subject)'}
          </div>
        </div>
        <div className="smartInbox_email_right">
          <div className="smartInbox_email_date">
            <div className="smartInbox_email_time">{time}</div>
            <div className="smartInbox_email_day">{date}</div>
          </div>
          {accountEmail && (
            <div className="smartInbox_email_account" title={`${isSentEmail ? 'Sent via' : 'Received via'} ${accountEmail}`}>
              <span className="smartInbox_email_account_icon">{isSentEmail ? '📤' : '📧'}</span>
              <span className="smartInbox_email_account_label">{displayAccountEmail}</span>
            </div>
          )}
        </div>
        
        {/* Hover-Lösch-Button */}
        <div className="smartInbox_email_hover_actions">
          {emailType === 'deleted' ? (
            /* Recover-Button für gelöschte E-Mails */
            <button 
              className="smartInbox_email_hover_recover"
              onClick={(e) => {
                e.stopPropagation(); // Verhindert das Öffnen der E-Mail
                handleRecover();
              }}
              disabled={deleting}
              title="Recover Email"
            >
              {deleting ? (
                <span className="delete_spinner"></span>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ) : (
            /* Normaler Delete-Button für nicht-gelöschte E-Mails */
            <button 
              className="smartInbox_email_hover_delete"
              onClick={(e) => {
                e.stopPropagation(); // Verhindert das Öffnen der E-Mail
                handleDelete();
              }}
              disabled={deleting}
              title="Delete"
            >
              {deleting ? (
                <span className="delete_spinner"></span>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                </svg>
              )}
            </button>
          )}
          
          {/* Tag-Management-Button - nur anzeigen wenn Tags vorhanden */}
          {hasEmailTags() && (
            <button 
              className="smartInbox_email_hover_tag"
              onClick={handleSetTagClick}
              title="Manage Tags"
            >
              {getFirstTagIcon() ? (
                <img 
                  src={getFirstTagIcon()} 
                  alt="Tag" 
                  className="tag_icon"
                />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" fill="currentColor"/>
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="smart_email_expanded_content">
          {/* Aktionsbuttons in der oberen rechten Ecke */}
          <div className="smart_email_actions">
            {/* Auto-Processing Status Indicator */}
            {!isSentEmail && autoProcessEnabled && !email.processedByAgent && (
              <div className="smart_email_auto_process_indicator" title="Auto-processing is enabled for new emails">
                <span className="auto_process_icon">⚡</span>
                <span className="auto_process_text">Auto-processing enabled</span>
              </div>
            )}
            
            {/* Löschen-Button für alle E-Mail-Typen anzeigen */}
            {emailType === 'deleted' ? (
              <button 
                className="smart_email_recover_button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRecover();
                }}
                disabled={deleting}
              >
                {deleting ? 'Recovering...' : 'Recover Email'}
              </button>
            ) : (
              <button 
                className="smart_email_delete_button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
          
          {/* Zusätzliche Informationen für gesendete E-Mails */}
          {renderSentEmailInfo()}
          
          {/* SafeGuard-Informationen für abgelehnte E-Mails */}
          {renderGenerationErrorInfo()}
          {renderCrmBlockedInfo()}
          {renderEmailInvalidInfo()}
          {renderReplyInterceptedInfo()}
          {renderSafeGuardServiceErrorInfo()}
          {renderSafeGuardInfo()}
          {renderPreSendScoreInfo()}
          
          {loading ? (
            <div className="email-loading-animation">
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <p className="loading-message"></p>
            </div>
          ) : (
            <div className="smart_email_body">
              {renderEmailContent()}
            </div>
          )}
          
          {/* Zeige Anhänge an, falls vorhanden */}
          {email.attachments && email.attachments.length > 0 && (
            <div className="smart_email_attachments">
              <h4>Attachments:</h4>
              <div className="smart_email_attachment_list">
                {email.attachments.map((attachment, index) => (
                  <div key={index} className="smart_email_attachment_item" data-attachment-index={index} onClick={() => downloadAttachment(index, attachment)}>
                    <span className="smart_email_attachment_icon">📎</span>
                    <span className="smart_email_attachment_name">{attachment.filename}</span>
                    <span className="smart_email_attachment_size">
                      ({Math.round(attachment.size / 1024)}KB)
                    </span>
                    <span className="smart_email_attachment_download">⬇️</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* EmailAiPopup über Portal rendern */}
      {renderEmailPopup()}
      
      {/* Forward E-Mail-Eingabe Modal */}
      {showForwardEmailInput && ReactDOM.createPortal(
        <div className="popupOverlayWrapper">
          <div className="popupOverlayBackground" onClick={handleForwardEmailCancel}></div>
          <div className="forwardEmailInputModal">
            <div className="forwardEmailInputHeader">
              <h3>Forward Email</h3>
              <button 
                className="forwardEmailInputClose" 
                onClick={handleForwardEmailCancel}
                title="Close"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="forwardEmailInputBody">
              <label htmlFor="forwardEmailAddress">Enter recipient email address:</label>
              <input
                id="forwardEmailAddress"
                type="email"
                placeholder="recipient@example.com"
                value={forwardEmailAddress}
                onChange={(e) => setForwardEmailAddress(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleForwardEmailSubmit();
                  }
                  if (e.key === 'Escape') {
                    handleForwardEmailCancel();
                  }
                }}
                autoFocus
                className="forwardEmailInput"
              />
            </div>
            <div className="forwardEmailInputActions">
              <button 
                className="forwardEmailCancelBtn" 
                onClick={handleForwardEmailCancel}
              >
                Cancel
              </button>
              <button 
                className="forwardEmailSubmitBtn" 
                onClick={handleForwardEmailSubmit}
              >
                Continue
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* E-Mail-Benachrichtigung über Portal am unteren Screen-Rand */}
      {emailNotifier.status && ReactDOM.createPortal(
        <EmailSentNotifier 
          status={emailNotifier.status}
          message={emailNotifier.message}
          onClose={resetEmailNotifier}
        />,
        document.body
      )}
      
      {/* SetTagPopup */}
      {showSetTagPopup && ReactDOM.createPortal(
        <SetTagPopup 
          email={email}
          userId={userId}
          onClose={handleSetTagPopupClose}
          onTagsUpdated={handleTagsUpdated}
        />,
        document.body
      )}
      
      {/* Add To Leads Modal mit Touchpoint-Auswahl */}
      {showAddToLeadsModal && ReactDOM.createPortal(
        <div className="add-to-leads-modal-overlay" onClick={() => {
          setShowAddToLeadsModal(false);
          setSelectedTouchpoints({});
        }}>
          <div className="add-to-leads-modal" onClick={(e) => e.stopPropagation()}>
            <div className="add-to-leads-modal-header">
              <h3>Add to Leads</h3>
              <button 
                className="add-to-leads-modal-close"
                onClick={() => {
                  setShowAddToLeadsModal(false);
                  setSelectedTouchpoints({});
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            
            <div className="add-to-leads-modal-info">
              <div className="add-to-leads-contact-preview">
                <i className="fa fa-user-plus"></i>
                <span>{displayPerson.name || email.from}</span>
              </div>
              <p className="add-to-leads-hint">Select touchpoints that already happened with this lead (optional)</p>
            </div>
            
            <div className="add-to-leads-modal-body">
              {Object.entries(TOUCHPOINT_GROUPS).map(([groupKey, group]) => (
                <div key={groupKey} className="add-to-leads-touchpoint-group">
                  <div className="add-to-leads-group-header">
                    <i className={`${group.isBrand ? 'fab' : 'fa'} ${group.icon}`}></i>
                    <span>{group.label}</span>
                  </div>
                  <div className="add-to-leads-touchpoint-options">
                    {Object.entries(group.items).map(([key, config]) => (
                      <button
                        key={key}
                        className={`add-to-leads-touchpoint-option ${selectedTouchpoints[key] ? 'selected' : ''}`}
                        onClick={() => toggleTouchpoint(key, config)}
                      >
                        <i className={`${config.isBrand ? 'fab' : 'fa'} ${config.icon}`}></i>
                        <span>{config.label}</span>
                        {selectedTouchpoints[key] && (
                          <i className="fa fa-check add-to-leads-check"></i>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Custom Touchpoints Section */}
              {customTouchpoints.length > 0 && (
                <div className="add-to-leads-touchpoint-group add-to-leads-custom-group">
                  <div className="add-to-leads-group-header">
                    <i className="fa fa-star"></i>
                    <span>Custom Touchpoints</span>
                  </div>
                  <div className="add-to-leads-touchpoint-options">
                    {customTouchpoints.map((tp) => {
                      const key = `custom_${tp.id}`;
                      return (
                        <button
                          key={key}
                          className={`add-to-leads-touchpoint-option add-to-leads-custom-option ${selectedTouchpoints[key] ? 'selected' : ''}`}
                          onClick={() => toggleTouchpoint(key, { hasCount: tp.hasCount, points: tp.points })}
                        >
                          <i className={`fa ${tp.icon || 'fa-tag'}`}></i>
                          <span>{tp.label}</span>
                          {selectedTouchpoints[key] && (
                            <i className="fa fa-check add-to-leads-check"></i>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            <div className="add-to-leads-modal-footer">
              <button 
                className="add-to-leads-confirm-btn"
                onClick={() => handleAddToLeads(selectedTouchpoints)}
              >
                <span className="add-to-leads-confirm-text">
                  {Object.keys(selectedTouchpoints).length > 0 
                    ? `Add Lead with ${Object.keys(selectedTouchpoints).length} Touchpoint${Object.keys(selectedTouchpoints).length > 1 ? 's' : ''}`
                    : 'Add Lead'
                  }
                </span>
                <span className="add-to-leads-confirm-icon">
                  <img src="/img/plus_1.svg" alt="+" className="add-to-leads-confirm-plus-img" />
                </span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SmartInboxEmail; 